import React from 'react';
import { GetServerSidePropsContext, GetServerSidePropsResult } from 'next';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import * as seline from '@seline-analytics/web';

import { usePageContext } from '../../context/page-provider';
import PageLayout from '../../components/blocks/page-layout';
import ProductImages from '../../components/blocks/product-images';
import formatCurrency from '../../utils/format-currency';
import Button from '../../components/elements/button';
import PlusIcon from '../../icons/plus';
import ShoppingBagIcon from '../../icons/shopping-bag';
import { info } from '../../event-emitters/notifications';
import graphqlClient from '../../utils/graphql-client';
import { captureException } from '../_app';
import useBrowserEffect from '../../hooks/useBrowserEffect';
import { GetProductDocument, GetProductQuery } from '../../../generated/graphql';
import isValidObjectId from '../../utils/validate-objectid';
import CheckIcon from '../../icons/check';
import CrossIcon from '../../icons/cross';
import { FREE_SHIPPING_TRESHOLD } from '../../constants';
import { BackLink } from '../../components/blocks/back-link';
import { ProductCardBanner } from '../../components/blocks/product-card';
import { useTranslate } from '../../hooks/useTranslate';
import { useLocale } from '../../hooks/useLocale';

interface Props {
  product?: GetProductQuery['product'];
  error?: Error | null;
}

const ProductPolicies = (props: {
  policies: Array<{
    value: string;
    isApplicable: boolean;
  }>;
}) => {
  let { policies } = props;

  return (
    <div className="my-4">
      {policies.map((policy) => {
        return (
          <div
            className={classNames('font-medium my-1 flex items-center', {
              'text-success': policy.isApplicable,
              'text-red-500 line-through': !policy.isApplicable,
            })}
            key={policy.value}
          >
            {policy.isApplicable ? <CheckIcon className="w-4 h-4" /> : <CrossIcon className="w-4 h-4" />}
            <div className="ml-2">{policy.value}</div>
          </div>
        );
      })}
    </div>
  );
};

const ProductPage = (props: { product: NonNullable<GetProductQuery['product']> }) => {
  const { product } = props;
  const Router = useRouter();
  const { cart, addToCart } = usePageContext();
  const inStockItems = product.stock.filter((s) => !!s.amount);
  const [selectedSize, setSelectedSize] = React.useState<string>(() => {
    return inStockItems[0]?.size || 'OS';
  });
  const [amount, setAmount] = React.useState<number>(1);
  const translate = useTranslate();
  const locale = useLocale();

  useBrowserEffect(() => {
    seline.track('View product', {
      productName: product.name.nl,
      value: product.price / 100,
    });
  });

  const isInCart = React.useMemo(() => {
    const foundProduct = cart.find((p) => {
      return p.amount === amount && p.size === selectedSize && p.id === product._id;
    });

    return !!foundProduct;
  }, [cart, amount, selectedSize]);

  const handleAddToCart = () => {
    if (isInCart) {
      Router.push('/shopping-bag');
      return;
    }

    addToCart({
      id: product._id,
      amount,
      size: selectedSize,
    });

    seline.track('Add to cart', {
      productName: product.name.nl,
      quantity: 1,
      value: product.price / 100,
    });

    info({
      title: translate('notifications.productAddedTitle'),
      content: translate('notifications.productAddedContent', { productName: product.name[locale] }),
    });
  };

  const descriptionJSX = product.description[locale].split('\n').map((line, i) => {
    return (
      <p key={i} className="mb-2">
        {line}
      </p>
    );
  });

  const hasDiscount = !!Math.round(product.discount ?? 0);
  const productPrice = Math.round(hasDiscount ? product.price * (1 - product.discount / 100) : product.price);
  const isInStock = inStockItems.length > 0;
  const canRetour = !hasDiscount && !product.isOutlet && !product.isSale;

  const isSale = Boolean(hasDiscount || product.isSale || product.isOutlet);
  return (
    <React.Fragment>
      <PageLayout title={product.name[locale]} description={product.description[locale]} image={product.pictures[0]}>
        <BackLink
          pageName={product.category.name[locale]}
          href={{
            pathname: '/products',
            query: {
              category: product.category._id,
            },
          }}
        />

        <article className="block md:flex">
          <div className="relative w-full md:w-1/2">
            {isSale ? <ProductCardBanner>Sale</ProductCardBanner> : null}
            <ProductImages images={product.pictures} name={product.name[locale]} />
          </div>
          <div className="my-8 mx-0 md:my-0 md:mx-8 w-full md:w-1/2">
            <h1 className="block text-raw-umber text-4xl capitalize mb-2">{product.name[locale]}</h1>

            <div className="flex text-2xl mb-4">
              {hasDiscount && (
                <span className="block text-desert-sand line-through mr-2">
                  {formatCurrency(product.price / 100, false)}
                </span>
              )}
              <span
                className={classNames('block text-raw-umber', {
                  'font-semibold': hasDiscount,
                })}
              >
                {formatCurrency(productPrice / 100, false)}
              </span>
            </div>

            <div className="mb-4">
              <div className="text-raw-umber font-medium capitalize text-lg">{translate('size')}</div>
              <div className="flex flex-wrap mt-1">
                {inStockItems
                  .sort((a, b) => a.displaySize.localeCompare(b.displaySize))
                  .map((s) => {
                    const handleSelectSize = () => {
                      setSelectedSize(s.size);
                    };

                    return (
                      <button
                        className={classNames(
                          'uppercase px-2 py-1 text-lg font-bold border-2 bg-isabelline border-transparent focus:ring mr-2',
                          {
                            'border-cambridge-blue-200 text-cambridge-blue-200': selectedSize === s.size,
                            'text-raw-umber': selectedSize !== s.size,
                          },
                        )}
                        onClick={(e) => {
                          e.preventDefault();
                          handleSelectSize();
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleSelectSize();
                          }
                        }}
                        key={s.size}
                      >
                        {s.displaySize}
                      </button>
                    );
                  })}
              </div>
            </div>

            <div className="flex w-full">
              {isInStock ? (
                <Button className="w-full" onSubmit={handleAddToCart}>
                  {isInCart ? (
                    <>
                      <ShoppingBagIcon className="mr-2 h-4 w-4" />
                      {translate('toCart')}
                    </>
                  ) : (
                    <>
                      <PlusIcon className="mr-2 h-4 w-4" />
                      {translate('addToCart')}
                    </>
                  )}
                </Button>
              ) : (
                <Button className="w-full" isDisabled>
                  Sold out!
                </Button>
              )}
            </div>

            <div className="mt-6">
              <ProductPolicies
                policies={[
                  {
                    value: translate('freeShipping', { treshold: '' + FREE_SHIPPING_TRESHOLD }),
                    isApplicable: true,
                  },
                  {
                    value: translate('reflectionPeriod'),
                    isApplicable: canRetour,
                  },
                ]}
              />

              <div className="my-8">
                <h2 className="mb-2 font-medium text-raw-umber text-lg">{translate('description')}</h2>
                <div className="text-raw-umber">{descriptionJSX}</div>
              </div>
            </div>
          </div>
        </article>
      </PageLayout>
    </React.Fragment>
  );
};

export default function Page(props: Props) {
  let { product } = props;
  if (!product) {
    return null;
  }
  return <ProductPage product={product} />;
}

export const getServerSideProps = async (ctx: GetServerSidePropsContext): Promise<GetServerSidePropsResult<Props>> => {
  let { params, res } = ctx;
  let id: string = params?.id && typeof params.id === 'string' ? params.id : '';
  if (!isValidObjectId(id)) {
    return {
      redirect: {
        statusCode: 302,
        destination: '/products',
      },
    };
  }

  let product = null;
  let error = null;
  try {
    let res = await graphqlClient.request<GetProductQuery>(GetProductDocument, { id });

    product = res.product!;
  } catch (err) {
    captureException(err);
    error = err;
  }

  res.setHeader('Cache-Control', 'public, s-maxage=10, stale-while-revalidate=59');

  return {
    props: {
      product,
      error: error as Error | null,
    },
  };
};

export const runtime = 'experimental-edge';
